import { useCallback } from 'react';
import { toNumber } from 'lodash';

import { Address } from '@/common/types';
import { IProviderAddressInfoDtoAddressTypeEnum } from '@/dtos';
import { UpdatedProviderDto, UpdateProviderDto } from '@/modules/UseMyMoney/queries';
import { useDocumentOcrStore } from '@/modules/UseMyMoney/stores';
import { OcrProviderOption } from '@/modules/UseMyMoney/UseMyMoney.types';
import { useInvalidateOcrQuery } from '@/modules/UseMyMoney/widgets/UseMyMoneySummary/hooks';
import { getFullAddress } from '@/utils';

interface Props {
  provider: OcrProviderOption;
  updateProvider: (createProviderDto: UpdateProviderDto) => Promise<UpdatedProviderDto>;
}

const useChangeProviderAddress = ({ provider, updateProvider }: Props) => {
  const { invalidateOcrQuery } = useInvalidateOcrQuery();
  const { ocrDocument } = useDocumentOcrStore();

  const handleChangeProviderAddress = useCallback(async (newAddressKey: string) => {
    if (!provider?.id) return;
    const newAddressOption = ocrDocument?.addressOptions?.find((addressOption) => {
      const addressOptionKey = getFullAddress({
        line1: addressOption?.line1,
        city: addressOption?.city,
        state: {
          id: addressOption?.state?.id,
          code: addressOption?.state?.code,
          name: addressOption?.state?.name,
        },
        zipcode: addressOption?.zipcode,
      });
      return newAddressKey === addressOptionKey;
    }) as Required<Address>;
    if (!newAddressOption) return;
    await updateProvider({
      providerId: provider?.id,
      facility_name: provider?.name,
      addresses: [{
        line1: newAddressOption?.line1,
        line2: newAddressOption?.line2,
        city: newAddressOption?.city,
        address_type: IProviderAddressInfoDtoAddressTypeEnum.RESIDENTIAL,
        validated: false,
        state: {
          id: toNumber(newAddressOption?.state?.id),
          code: newAddressOption?.state?.code,
          name: newAddressOption?.state?.name,
        },
        zipcode: newAddressOption?.zipcode,
      }],
    });
    await invalidateOcrQuery();
  }, [ocrDocument?.addressOptions, provider, updateProvider, invalidateOcrQuery]);

  return {
    handleChangeProviderAddress,
  };
};

export default useChangeProviderAddress;
